import { useEffect, useState } from 'react';
import { dashboardService, alertsService } from '../services';

/**
 * The numbers the side nav is allowed to show.
 *
 * ── Why this exists ──
 * The nav used to compute its one badge in the component, by scanning whatever
 * leads the CRM context happened to have cached — page 0, size 100 — and
 * counting "hot" ones. Three problems with that, and only one of them was the
 * arithmetic:
 *
 *   1. It counted an OPPORTUNITY. "Three leads are scoring well" is not a
 *      reason to click anything; there is no action that resolves it, so the
 *      badge never reached zero and became furniture. A nav badge should mean
 *      "work is waiting here", which is a thing you can finish.
 *
 *   2. It disagreed with the dashboard. The nav said 3 while the dashboard —
 *      one click away, describing the same dealership — said 5 unactioned and
 *      1 with no next step. Two surfaces, two answers, no way to reconcile
 *      them. Now both read the same endpoint, so they cannot drift.
 *
 *   3. It was capped at the first 100 leads and silently undercounted past
 *      that. These counts are computed server-side over the whole dealership,
 *      so the cap is gone.
 *
 * ── Why the role-snapshot and not a lead scan ──
 * The exception counts are already defined, tested and sales-segregated on the
 * server (E2). Re-deriving them in the client would be a second definition of
 * "unactioned" that drifts from the first the moment either changes.
 *
 * ── Failure behaviour ──
 * On error this returns nulls and the nav renders NO badges. A nav badge is a
 * claim about the dealership; a stale or guessed one sends someone to the
 * wrong screen. Showing nothing is the honest failure, and it is quiet — this
 * polls in the background and its failures are not the user's problem.
 */

const POLL_MS = 60_000;      // The nav is ambient. The dashboard is the live view.
const WINDOW_DAYS = 7;       // Only affects `appointmentsToday`; exceptions are "now".

const EMPTY = {
  needsAttention: null,
  unactioned: 0,
  noNextStep: 0,
  unassigned: 0,
  nurtureExpiring: 0,
  overdueTasks: null,
  appointmentsToday: null,
};

/**
 * @param watch  an opaque string that changes when something the counts depend
 *               on has changed — see SideNav's lead-stage signature. Polling
 *               alone is not good enough: a user who marks a lead lost and
 *               watches the nav not move concludes the number is fake, and
 *               they are right to. A minute of lag on your OWN action reads as
 *               broken, however correct the number turns out to be.
 */
export function useNavCounts({ role, consultantId, watch = null }) {
  const [counts, setCounts] = useState(EMPTY);

  useEffect(() => {
    if (!role) return undefined;
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        const rs = await dashboardService.roleSnapshot({
          role,
          days: WINDOW_DAYS,
          consultantId: role === 'SALES_CONSULTANT' ? (consultantId || undefined) : undefined,
        });
        if (cancelled) return;
        const ex = rs?.exceptions;
        if (!ex) { setCounts(EMPTY); return; }

        const unactioned = ex.unactionedLeads ?? 0;
        const noNextStep = ex.leadsWithoutNextEvent ?? 0;
        const unassigned = ex.unassignedLeads ?? 0;
        const nurtureExpiring = ex.aiNurtureExpiringSoon ?? 0;

        // The badge must equal the Lead Accelerator's "Needs attention" count —
        // the set of DISTINCT flagged leads, from the same endpoint the board
        // uses. The old value summed four overlapping exception categories, so
        // one lead could be counted several times and the badge could exceed the
        // total number of leads (DEF: badge 17 vs 12 leads / 7 needing attention).
        let needsAttention = 0;
        try {
          const flagged = await alertsService.unreadLeadIds();
          needsAttention = Array.isArray(flagged) ? flagged.length : 0;
        } catch { /* leave at 0 — no badge beats a wrong one */ }
        if (cancelled) return;

        setCounts({
          needsAttention,
          // Kept for the hover breakdown / other callers; NOT summed into the badge.
          unactioned, noNextStep, unassigned, nurtureExpiring,
          overdueTasks: ex.overdueTasks ?? 0,
          appointmentsToday: rs?.today?.appointments ?? null,
        });
      } catch (_) {
        if (!cancelled) setCounts(EMPTY);   // no badge beats a wrong badge
      }
    };

    load();
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    timer = setInterval(tick, POLL_MS);
    // Coming back to the tab should not mean staring at a minute-old number.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [role, consultantId, watch]);

  return counts;
}
